import { useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api/client.js';
import { useStepUp, StepUpPrompt } from '../../lib/auth/stepup.js';
import { CardHead, Empty, ErrorBanner } from '../../components/ui/Primitives.js';
import { confirmAction } from '../../components/ui/Confirm.js';
import { relativeTime } from '../../lib/format.js';

/**
 * Linking the ArcNet Chrome extension to a seat with the code it shows (browserlink, 0179), here in
 * the TMS while ArcSIP's scanner is not built (owner, 31 October 2026). The same approval as the
 * app's: the browser can then ring that seat and dial as it, and nothing else.
 */

interface Seat { id: string; number: string; display_name: string | null; account: string }
interface Linked { id: string; label: string; extension: string; account: string; created_at: string; last_used_at: string | null }

export function LinkBrowserCard() {
  const qc = useQueryClient();
  const step = useStepUp();
  const [code, setCode] = useState('');
  const [asked, setAsked] = useState<string | null>(null);
  const [seat, setSeat] = useState('');
  const [done, setDone] = useState<string | null>(null);
  const clean = code.toUpperCase().replace(/[\s-]/g, '');

  const pairing = useQuery({
    queryKey: ['browser-pairing', asked],
    queryFn: () => api.get<{ label: string; seats: Seat[] }>(`/v1/me/browser-pairings/${asked}`),
    enabled: !!asked, retry: false,
  });
  const links = useQuery({ queryKey: ['me-browser-links'], queryFn: () => api.get<{ data: Linked[] }>('/v1/me/browser-links') });
  const approve = useMutation({
    mutationFn: (extensionId: string) => api.post(`/v1/me/browser-pairings/${asked}/approve`, { extension_id: extensionId }),
    onSuccess: (_r, extensionId) => {
      const s = pairing.data?.seats.find((x) => x.id === extensionId);
      setDone(`${pairing.data?.label ?? 'The browser'} can now call from ${s ? s.number : 'that extension'}.`);
      setAsked(null); setCode(''); setSeat('');
      void qc.invalidateQueries({ queryKey: ['me-browser-links'] });
    },
  });
  const unlink = useMutation({
    mutationFn: (id: string) => api.delete(`/v1/me/browser-links/${id}`),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['me-browser-links'] }),
  });

  const seats = pairing.data?.seats ?? [];
  const chosen = seat || seats[0]?.id || '';

  return (
    <div className="arc-card" style={{ maxWidth: 720, marginTop: 16 }}>
      <CardHead title="Link a browser"
        sub="For the ArcNet Chrome extension: enter the code it shows, and it can ring your phone and dial numbers you click." />
      <div className="arc-card-body arc-stack" style={{ gap: 12 }}>
        {done && (
          <div className="arc-banner arc-banner-info" role="status">
            <span style={{ flex: 1 }}>{done}</span>
            <button type="button" className="arc-btn arc-btn-ghost arc-btn-sm" onClick={() => setDone(null)}>Dismiss</button>
          </div>
        )}
        {!asked ? (
          <form className="arc-row" style={{ gap: 8, alignItems: 'end' }}
            onSubmit={(e: FormEvent) => { e.preventDefault(); if (clean.length === 8) { setDone(null); setAsked(clean); } }}>
            <div className="arc-field" style={{ margin: 0, width: 220 }}>
              <label className="arc-label" htmlFor="link-code">Code from the extension</label>
              <input id="link-code" className="arc-input arc-mono" value={code} maxLength={11} autoComplete="off"
                placeholder="K7P2 QXAB" onChange={(e) => setCode(e.target.value)} style={{ textTransform: 'uppercase', letterSpacing: '.08em' }} />
            </div>
            <button type="submit" className="arc-btn arc-btn-primary" disabled={clean.length !== 8}>Continue</button>
          </form>
        ) : pairing.error ? (
          <>
            <ErrorBanner error={pairing.error} />
            <div><button type="button" className="arc-btn" onClick={() => setAsked(null)}>Try another code</button></div>
          </>
        ) : !pairing.data ? (
          <div className="arc-skeleton" style={{ width: 260, height: 14 }} />
        ) : seats.length === 0 ? (
          <>
            <p style={{ margin: 0 }}>You have no extension to link a browser to. Ask your company's ArcNet admin to give you one.</p>
            <div><button type="button" className="arc-btn" onClick={() => setAsked(null)}>Cancel</button></div>
          </>
        ) : (
          <form className="arc-stack" style={{ gap: 10 }} onSubmit={(e: FormEvent) => {
            e.preventDefault();
            void step.run(() => approve.mutateAsync(chosen));
          }}>
            <p style={{ margin: 0 }}>Let <strong>{pairing.data.label}</strong> call from:</p>
            <div className="arc-field" style={{ margin: 0, maxWidth: 420 }}>
              <label className="arc-label" htmlFor="link-seat">Extension</label>
              <select id="link-seat" className="arc-select" value={chosen} onChange={(e) => setSeat(e.target.value)}>
                {seats.map((s) => <option key={s.id} value={s.id}>{s.number}{s.display_name ? ` ${s.display_name}` : ''}, {s.account}</option>)}
              </select>
            </div>
            <p className="arc-subtle" style={{ margin: 0, fontSize: 12.5 }}>It can ring that phone and dial numbers as it, under its own caller ID and limits. Nothing else.</p>
            {approve.error && <ErrorBanner error={approve.error} />}
            <div className="arc-row" style={{ gap: 8 }}>
              <button type="submit" className="arc-btn arc-btn-primary" disabled={approve.isPending || step.busy}>
                {approve.isPending || step.busy ? 'Linking…' : 'Approve'}
              </button>
              <button type="button" className="arc-btn" onClick={() => setAsked(null)}>Cancel</button>
            </div>
          </form>
        )}

        {links.data && links.data.data.length > 0 && (
          <div className="arc-table-wrap">
            <table className="arc-table">
              <thead><tr><th>Browser</th><th>Extension</th><th>Last used</th><th /></tr></thead>
              <tbody>
                {links.data.data.map((l) => (
                  <tr key={l.id}>
                    <td>{l.label}</td>
                    <td><span className="arc-mono">{l.extension}</span> <span className="arc-subtle">{l.account}</span></td>
                    <td className="arc-num">{l.last_used_at ? relativeTime(l.last_used_at) : 'never'}</td>
                    <td className="arc-td-right">
                      <button type="button" className="arc-btn arc-btn-ghost arc-btn-sm"
                        onClick={() => { void confirmAction({ title: `Unlink ${l.label}?`, body: 'It stops working at once.', action: 'Unlink', danger: true }).then((ok) => { if (ok) unlink.mutate(l.id); }); }}>
                        Unlink
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {links.data && links.data.data.length === 0 && !asked && (
          <Empty icon="external" title="No browsers linked" hint="Install the ArcNet Chrome extension and choose Link to get a code." />
        )}
        {unlink.error && <ErrorBanner error={unlink.error} />}
      </div>
      <StepUpPrompt step={step} what="Linking a browser">
        The browser will be able to ring that extension and dial out as it.
      </StepUpPrompt>
    </div>
  );
}
